import { useEffect } from "react";
import { Stack, IconButton, Select, Option } from "@introist/react-foundation/v2";

// Curated list of popular Google Fonts
const POPULAR_FONTS = [
  "Open Sans",
  "Roboto",
  "Lato",
  "Montserrat",
  "Oswald",
  "Source Sans Pro",
  "Raleway",
  "PT Sans",
  "Merriweather",
  "Nunito",
  "Playfair Display",
  "Poppins",
  "Ubuntu",
  "Mukta",
  "Rubik",
  "Work Sans",
  "Noto Sans",
  "Fira Sans",
  "Quicksand",
  "Karla",
  "Cabin",
  "Barlow",
  "Crimson Text",
  "Libre Baskerville",
  "Bebas Neue",
  "Dancing Script",
  "Pacifico",
  "Lobster",
  "Righteous",
  "Anton"
];

interface FontPickerWrapperProps {
  activeFontFamily?: string | null;
  onChange: (fontFamily: string | null) => void;
}

export const FontPickerWrapper = ({ activeFontFamily, onChange }: FontPickerWrapperProps) => {
  // Load Google Font when selected
  useEffect(() => {
    if (activeFontFamily) {
      const fontFamily = activeFontFamily;
      const linkId = `google-font-${fontFamily.replace(/\s+/g, "-")}`;

      // Check if font is already loaded
      if (document.getElementById(linkId)) {
        return;
      }

      // Create and append Google Fonts link
      const link = document.createElement("link");
      link.id = linkId;
      link.rel = "stylesheet";
      link.href = `https://fonts.googleapis.com/css2?family=${fontFamily.replace(
        / /g,
        "+"
      )}:wght@400;700&display=swap`;
      document.head.appendChild(link);

      // Cleanup on unmount
      return () => {
        const existingLink = document.getElementById(linkId);
        if (existingLink) {
          existingLink.remove();
        }
      };
    }
  }, [activeFontFamily]);

  const handleSelect = (option: Option) => {
    if (option) {
      onChange(option.key);
    }
  };

  const handleReset = () => {
    onChange(null);
  };

  // Convert font list to Select options format
  const fontOptions = POPULAR_FONTS.map(font => ({
    key: font,
    title: font
  }));

  return (
    <Stack gap="small" alignItems="center">
      <Select
        element="button"
        variant="outlined"
        value={activeFontFamily || undefined}
        onSelect={handleSelect}
        options={fontOptions}
        size="small"
        searchable
        placeholder="Customise font"
      />
      <IconButton
        icon="crossSmall"
        variant="blended"
        size="small"
        onClick={handleReset}
        aria-label="Reset to default font"
        style={{ visibility: activeFontFamily ? 'visible' : 'hidden' }}
      />
    </Stack>
  );
};
